import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiService } from '../services/api';
import { ReportData, ReportDateRange, TrialSavingsReportData } from '../types';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';
import {
  AlertOctagon,
  ArrowRight,
  BarChart3,
  Calendar,
  CheckCircle2,
  Download,
  FileText,
  Loader2,
  Lock,
  PoundSterling,
  RefreshCw,
  ShieldCheck,
  TrendingDown,
} from 'lucide-react';
import { useBranding } from '../context/BrandingContext';
import { useSubscription } from '../context/SubscriptionContext';
import { getErrorMessage } from '../services/supabaseClient';
import { useToast } from '../context/ToastContext';
import TrialSavingsReport from './TrialSavingsReport';

const REPORTS_REFRESH_INTERVAL_MS = 60_000;

type ReportsTab = 'pcn-reports' | 'trial-snapshot' | 'monthly-aggregate';

const validTabs: ReportsTab[] = ['pcn-reports', 'trial-snapshot', 'monthly-aggregate'];

const currency = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0,
});

const tabCopy: Record<ReportsTab, { label: string; description: string }> = {
  'pcn-reports': {
    label: 'PCN reports',
    description: 'Per-PCN readiness, liability, recovery, and evidence summaries.',
  },
  'trial-snapshot': {
    label: '3-PCN trial snapshot',
    description: 'Trial value snapshot and optional 10-PCN manual review flow.',
  },
  'monthly-aggregate': {
    label: 'Fleet analytics',
    description: 'Growth+ liability, appeal, authority, and trend analysis by date range.',
  },
};

const Reports: React.FC = () => {
  const { branding } = useBranding();
  const { tier, checkAccess } = useSubscription();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const hasMonthlyAggregate = checkAccess('advancedReports');
  const activeTab = useMemo<ReportsTab>(() => {
    const rawTab = searchParams.get('tab') as ReportsTab | null;
    return rawTab && validTabs.includes(rawTab) ? rawTab : 'pcn-reports';
  }, [searchParams]);

  const [data, setData] = useState<ReportData | null>(null);
  const [pcnReport, setPcnReport] = useState<TrialSavingsReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [pcnLoading, setPcnLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pcnError, setPcnError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<ReportDateRange>('Last 6 Months');
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);

  const setActiveTab = (tab: ReportsTab) => {
    setSearchParams({ tab });
  };

  const fetchMonthlyAggregate = async (opts?: { silent?: boolean }) => {
    if (!hasMonthlyAggregate) return;

    const silent = !!opts?.silent;
    try {
      if (silent) setRefreshing(true);
      else {
        setLoading(true);
        setError(null);
      }

      const res = await ApiService.getReports(dateRange);
      setData(res);
      setLastUpdatedAt(new Date().toISOString());
    } catch (e) {
      if (!silent) setError('Unable to load fleet analytics. Please refresh or try again later.');
      console.warn('Fleet analytics load failed:', e);
      addToast(silent ? 'Fleet analytics refresh failed.' : 'Unable to load fleet analytics.', 'error');
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  };

  const fetchPcnReport = async () => {
    try {
      setPcnLoading(true);
      setPcnError(null);
      const res = await ApiService.getTrialSavingsReport('3pcn_snapshot');
      setPcnReport(res);
    } catch (e) {
      const msg = getErrorMessage(e);
      setPcnError(msg);
      addToast(msg, 'error');
    } finally {
      setPcnLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'monthly-aggregate' && hasMonthlyAggregate) {
      void fetchMonthlyAggregate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dateRange, hasMonthlyAggregate]);

  useEffect(() => {
    if (activeTab === 'pcn-reports' && !pcnReport) {
      void fetchPcnReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'monthly-aggregate' || !hasMonthlyAggregate) return;

    const interval = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void fetchMonthlyAggregate({ silent: true });
    }, REPORTS_REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dateRange, hasMonthlyAggregate]);

  const exportMonthlyCsv = () => {
    if (!data) return;
    const escapeCell = (value: unknown) => {
      const s = String(value ?? '');
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const rows: Array<Array<string | number>> = [
      ['Metric', 'Value'],
      ['Total Liability', data.kpis.totalSpend],
      ['Total Saved', data.kpis.totalSaved],
      ['Avg Ticket Cost', data.kpis.avgTicketCost],
      ['Appeal Success Rate (%)', data.kpis.appealSuccessRate],
      [],
      ['Spending Trend'],
      ['Month', 'Actual', 'Projected'],
      ...data.spendingTrend.map(r => [r.month, r.actual, r.projected].map(String)),
      [],
      ['Top Issuing Authorities'],
      ['Authority', 'Liability'],
      ...data.costByAuthority.map(r => [r.name, String(r.value)]),
      [],
      ['Contravention Types'],
      ['Type', 'Count'],
      ...data.contraventionTypes.map(r => [r.name, String(r.count)]),
      [],
      ['Appeal Performance'],
      ['Outcome', 'Value'],
      ...data.appealPerformance.map(r => [r.name, String(r.value)])
    ];

    const csv = rows.map(r => r.map(escapeCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `pcnguard-monthly-aggregate-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exportPcnCsv = () => {
    if (!pcnReport) return;
    const header = ['PCN', 'VRN', 'Authority', 'Amount', 'Deadline risk', 'Liability ready', 'Recovery visible', 'Evidence gap'];
    const rows = pcnReport.rows.map(row => [
      row.pcnNumber,
      row.vrn,
      row.authority,
      row.amount,
      row.deadlineRisk,
      row.liabilityReady ? 'yes' : 'no',
      row.recoveryVisible ? 'yes' : 'no',
      row.evidenceGap || '',
    ]);
    const csv = [header, ...rows]
      .map(line => line.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fleetmode-pcn-reports-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const BRAND_COLOR = branding.primaryColor;

  const renderPcnReports = () => {
    if (pcnLoading && !pcnReport) {
      return (
        <div className="flex h-72 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      );
    }

    if (pcnError) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200">
          {pcnError}
        </div>
      );
    }

    if (!pcnReport) return null;

    const rows = pcnReport.rows;
    const missing = Math.max(pcnReport.targetCount - pcnReport.availablePcnCount, 0);

    return (
      <div className="space-y-5">
        <section className="grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">PCNs included</p>
            <p className="mt-3 text-3xl font-bold text-slate-950 dark:text-white">{rows.length}/{pcnReport.targetCount}</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {missing > 0 ? `${missing} more processed PCN${missing === 1 ? '' : 's'} needed for the full snapshot.` : 'Snapshot sample complete.'}
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Cost exposure</p>
            <p className="mt-3 text-3xl font-bold text-slate-950 dark:text-white">{currency.format(pcnReport.metrics.totalCostExposure)}</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Estimated from saved PCN values.</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Readiness</p>
            <p className="mt-3 text-3xl font-bold text-slate-950 dark:text-white">{pcnReport.metrics.liabilityReadyCount}/{rows.length || 0}</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">PCNs with visible driver or hirer responsibility.</p>
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col gap-3 border-b border-slate-200 p-5 dark:border-slate-800 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-950 dark:text-white">Per-PCN operational reports</h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Starter includes this per-PCN view. Fleet analytics unlocks on Growth.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={fetchPcnReport}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <RefreshCw size={15} />
                Refresh
              </button>
              <button
                onClick={exportPcnCsv}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-950 px-3 py-2 text-sm font-bold text-white hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
              >
                <Download size={15} />
                CSV
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
              <thead className="bg-slate-50 dark:bg-slate-950/50">
                <tr>
                  {['PCN', 'Vehicle', 'Exposure', 'Deadline', 'Liability', 'Recovery', 'Gap'].map((heading) => (
                    <th key={heading} className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {rows.map((row) => (
                  <tr key={row.id} className="align-top">
                    <td className="px-4 py-4">
                      <p className="font-bold text-slate-950 dark:text-white">{row.pcnNumber}</p>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{row.authority}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="font-semibold text-slate-800 dark:text-slate-200">{row.vrn || 'Unknown'}</p>
                      <p className="mt-1 max-w-[220px] text-xs text-slate-500 dark:text-slate-400">{row.location}</p>
                    </td>
                    <td className="px-4 py-4 font-bold text-slate-950 dark:text-white">{currency.format(row.amount)}</td>
                    <td className="px-4 py-4">
                      <span className={`rounded-full px-2 py-1 text-xs font-bold capitalize ${
                        row.deadlineRisk === 'high'
                          ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-200'
                          : row.deadlineRisk === 'medium'
                            ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200'
                            : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200'
                      }`}>
                        {row.deadlineRisk}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center gap-1 text-xs font-bold ${row.liabilityReady ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-500 dark:text-slate-400'}`}>
                        {row.liabilityReady ? <CheckCircle2 size={14} /> : <AlertOctagon size={14} />}
                        {row.liabilityReady ? row.driverOrHirer : 'Unassigned'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`text-xs font-bold ${row.recoveryVisible ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}`}>
                        {row.recoveryVisible ? 'Visible' : 'Not visible'}
                      </span>
                    </td>
                    <td className="max-w-xs px-4 py-4 text-xs text-slate-500 dark:text-slate-400">{row.evidenceGap || 'None'}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
                      No processed PCNs are available for reporting yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    );
  };

  const renderMonthlyLocked = () => (
    <section className="rounded-lg border border-amber-200 bg-amber-50 p-6 dark:border-amber-900/60 dark:bg-amber-950/30">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">
            <Lock size={14} />
            Growth feature
          </div>
          <h2 className="mt-3 text-xl font-bold text-amber-950 dark:text-amber-100">Fleet analytics is not included on Starter</h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-amber-900 dark:text-amber-200">
            Starter keeps per-PCN reports and the 3-PCN trial snapshot. Upgrade to Growth for liability trends, repeat driver/location patterns, recovery tracking, and management-level analysis.
          </p>
        </div>
        <button
          onClick={() => navigate('/pricing')}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-amber-950"
        >
          Compare Growth
          <ArrowRight size={16} />
        </button>
      </div>
    </section>
  );

  const renderMonthlyAggregate = () => {
    if (!hasMonthlyAggregate) return renderMonthlyLocked();

    if (loading) {
      return (
        <div className="flex h-96 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-6 text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-400">
          <AlertOctagon className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <div>
            <h3 className="font-medium">Unable to load fleet analytics</h3>
            <p className="mt-1 text-sm opacity-90">{error}</p>
          </div>
        </div>
      );
    }

    if (!data) return null;

    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Fleet analytics</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Financial intelligence and liability trends.
              {lastUpdatedAt && (
                <span className="ml-3 text-xs">
                  Last updated {new Date(lastUpdatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  <span className="ml-2 text-slate-400">- Auto-refresh: 60s</span>
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => fetchMonthlyAggregate({ silent: true })}
              disabled={refreshing}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              {refreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              Refresh
            </button>
            <div className="relative">
              <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as ReportDateRange)}
                className="appearance-none rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-4 text-sm font-medium text-slate-600 outline-none transition-colors hover:bg-slate-50 focus:ring-2 focus:ring-primary-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <option>Last 30 Days</option>
                <option>Last Quarter</option>
                <option>Last 6 Months</option>
                <option>Year to Date</option>
              </select>
            </div>
            <button
              onClick={exportMonthlyCsv}
              className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
            >
              <Download size={16} /> Export CSV
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Total Liability', value: `£${data.kpis.totalSpend.toLocaleString()}`, icon: TrendingDown, helper: '12% vs last period', tone: 'text-slate-600 dark:text-slate-400' },
            { label: 'Total Saved', value: `£${data.kpis.totalSaved.toLocaleString()}`, icon: ShieldCheck, helper: 'Via appeals & transfers', tone: 'text-green-600 dark:text-green-400' },
            { label: 'Avg. Ticket Cost', value: `£${data.kpis.avgTicketCost.toFixed(2)}`, icon: AlertOctagon, helper: 'After discount applied', tone: 'text-amber-600 dark:text-amber-400' },
            { label: 'Appeal Success', value: `${data.kpis.appealSuccessRate}%`, icon: ShieldCheck, helper: 'Top 10% of fleets', tone: 'text-blue-600 dark:text-blue-400' },
          ].map((item) => (
            <div key={item.label} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm transition-colors dark:border-slate-800 dark:bg-slate-900">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{item.label}</h3>
                <div className={`rounded-lg bg-slate-100 p-2 dark:bg-slate-800 ${item.tone}`}>
                  <item.icon size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{item.value}</p>
              <span className={`mt-1 block text-xs font-medium ${item.tone}`}>{item.helper}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm transition-colors dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-6">
              <h3 className="text-lg font-bold text-slate-800 dark:text-white">Liability Trends</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">Actual Spend vs. Projected Liability</p>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.spendingTrend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={BRAND_COLOR} stopOpacity={0.1}/>
                      <stop offset="95%" stopColor={BRAND_COLOR} stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorProjected" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#94a3b8" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#334155" opacity={0.2} />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', backgroundColor: 'var(--color-slate-900, #0f172a)', color: '#fff' }} cursor={{ stroke: '#cbd5e1' }} />
                  <Area type="monotone" dataKey="projected" stroke="#94a3b8" strokeDasharray="5 5" fillOpacity={1} fill="url(#colorProjected)" name="Projected" />
                  <Area type="monotone" dataKey="actual" stroke={BRAND_COLOR} fillOpacity={1} fill="url(#colorActual)" name="Actual Spend" />
                  <Legend verticalAlign="top" height={36} iconType="circle" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm transition-colors dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-6">
              <h3 className="text-lg font-bold text-slate-800 dark:text-white">Contravention Breakdown</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">Most frequent violation types</p>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.contraventionTypes} layout="vertical" margin={{ top: 0, right: 30, left: 40, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#334155" opacity={0.2} />
                  <XAxis type="number" hide />
                  <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                  <Tooltip cursor={{ fill: '#334155', opacity: 0.1 }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', backgroundColor: 'var(--color-slate-900, #0f172a)', color: '#fff' }} />
                  <Bar dataKey="count" fill={BRAND_COLOR} radius={[0, 4, 4, 0]} barSize={24} name="Tickets" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm transition-colors dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-6">
              <h3 className="text-lg font-bold text-slate-800 dark:text-white">Appeal Performance</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">Outcomes of contested tickets</p>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.appealPerformance} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" stroke="none">
                    {data.appealPerformance.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', backgroundColor: 'var(--color-slate-900, #0f172a)', color: '#fff' }} />
                  <Legend verticalAlign="bottom" height={36} iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm transition-colors dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-6">
              <h3 className="text-lg font-bold text-slate-800 dark:text-white">Top Issuing Authorities</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">Liability by council</p>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.costByAuthority}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#334155" opacity={0.2} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} interval={0} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                  <Tooltip cursor={{ fill: '#334155', opacity: 0.1 }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', backgroundColor: 'var(--color-slate-900, #0f172a)', color: '#fff' }} />
                  <Bar dataKey="value" fill={BRAND_COLOR} radius={[4, 4, 0, 0]} name="Liability (£)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary-700 dark:border-primary-900/60 dark:bg-primary-950/30 dark:text-primary-300">
            <BarChart3 size={14} />
            Reports
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">FleetMode reports</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
            Per-PCN reporting is available on Starter. Fleet analytics unlocks on Growth and above.
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
          Current plan: <span className="font-bold capitalize text-slate-950 dark:text-white">{tier === 'pro_fleet' ? 'Pro Fleet' : String(tier).replace('_', ' ')}</span>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-3">
        {validTabs.map((tab) => {
          const active = activeTab === tab;
          const locked = tab === 'monthly-aggregate' && !hasMonthlyAggregate;
          const Icon = tab === 'monthly-aggregate' ? BarChart3 : tab === 'trial-snapshot' ? FileText : PoundSterling;
          const activeText = active ? 'text-white dark:text-slate-950' : 'text-slate-950 dark:text-white';
          const activeMutedText = active ? 'text-slate-200 dark:text-slate-700' : 'text-slate-500 dark:text-slate-400';
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`rounded-lg border p-4 text-left transition ${
                active
                  ? 'border-slate-950 bg-slate-950 text-white shadow-sm ring-2 ring-primary-300 dark:border-white dark:bg-white dark:text-slate-950 dark:ring-primary-500'
                  : 'border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Icon size={18} className={activeText} />
                  <span className={`font-bold ${activeText}`}>{tabCopy[tab].label}</span>
                </div>
                {locked && <Lock size={15} className="text-amber-600" />}
              </div>
              <p className={`mt-2 text-sm leading-5 ${activeMutedText}`}>{tabCopy[tab].description}</p>
            </button>
          );
        })}
      </div>

      {activeTab === 'pcn-reports' && renderPcnReports()}
      {activeTab === 'trial-snapshot' && <TrialSavingsReport embedded />}
      {activeTab === 'monthly-aggregate' && renderMonthlyAggregate()}
    </div>
  );
};

export default Reports;
